import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAISystemOverview } from '../../../services/AISystemsService';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  fetchCurrentOverviewFailure,
  fetchCurrentOverviewStart,
  fetchCurrentOverviewSuccess,
  setCurrentOverviewAiSystemId,
  setHasMajorSeverity
} from '../../../store/slices/aiSystemsSlice';
import { Divider } from '../../ui/Divider/Divider';
import { OverviewCard } from '../../ui/OverviewCard/OverviewCard';
import { GeneralInformation } from '../GeneralInformation/GeneralInformation';
import { Users } from '../Users/Users';

const formatEnumLabel = (value?: string | null, separator = ' ') => {
  if (!value) return 'Unknown';

  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(separator);
};

export function AISystemOverviewTab() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentOverview, currentOverviewLoading, currentOverviewAiSystemId } =
    useAppSelector((state) => state.aiSystems);

  useEffect(() => {
    const fetchOverviewData = async () => {
      if (!id) return;
      if (currentOverview && currentOverviewAiSystemId === id) return;

      try {
        dispatch(fetchCurrentOverviewStart());
        dispatch(setCurrentOverviewAiSystemId(id));
        const data = await getAISystemOverview(id);
        dispatch(fetchCurrentOverviewSuccess(data));
        dispatch(setHasMajorSeverity(data.repositories.hasMajor));
      } catch (error) {
        console.error('Failed to fetch overview data:', error);
        dispatch(fetchCurrentOverviewFailure('Failed to fetch overview data'));
      }
    };

    fetchOverviewData();
  }, [id, currentOverview, currentOverviewAiSystemId, dispatch]);

  if (currentOverviewLoading || !currentOverview) {
    return <div>Loading...</div>;
  }

  const riskClassificationCard = currentOverview.riskClassification
    ? {
        title: 'Risk classification',
        variant: 'classification' as const,
        riskLevel: currentOverview.riskClassification
          .toLowerCase()
          .replaceAll('_', '-') as any,
        riskText: formatEnumLabel(currentOverview.riskClassification, '-')
      }
    : {
        title: 'Risk classification',
        value: '--'
      };

  const overviewCards = [
    {
      title: 'REQUIREMENTS',
      value: currentOverview.requirements.pendingSteps.toString(),
      subtitle: `of ${currentOverview.requirements.totalSteps} pending`
    },
    {
      title: 'DOCUMENTATION',
      value: currentOverview.documentation.completed.toString(),
      subtitle: `of ${currentOverview.documentation.total} completed`
    },
    {
      title: 'REPOSITORIES',
      value: currentOverview.repositories.count.toString()
    },
    {
      title: 'COMPLIANCE STATUS',
      variant: 'status' as const,
      statusText: formatEnumLabel(currentOverview.complianceStatus)
    },
    riskClassificationCard
  ];

  return (
    <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)]">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 flex-shrink-0">
        {overviewCards.map((card, index) => (
          <OverviewCard key={index} {...card} />
        ))}
      </div>
      <div className="flex flex-col gap-6 overflow-y-auto">
        <Divider classes="border-gray-300" />
        <GeneralInformation data={currentOverview.generalInfo} />
        <Divider classes="border-gray-300" />
        <Users data={currentOverview.users} />
      </div>
    </div>
  );
}
